"use client";
import { useRouter as usePagesRouter } from "next/compat/router";

import {
  useRouter as useAppRouter,
  usePathname,
  useSearchParams
} from "next/navigation";
import { useCallback, useMemo } from "react";

type NavOptions = {
  scroll?: boolean;
  shallow?: boolean;
};

type QueryValue = string | Array<string> | undefined;

function buildUrlFromPathAndQuery(
  pathname: string,
  query: Record<string, any> = {}
) {
  const parameters = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        parameters.append(key, String(item));
      }
    } else {
      parameters.set(key, String(value));
    }
  }

  const qs = parameters.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const useCompatRouter = () => {
  const pagesRouter = usePagesRouter();
  const appRouter = useAppRouter();
  const pathname = usePathname();
  const searchParameters = useSearchParams();

  const getAppCurrentQuery = useCallback(() => {
    if (!searchParameters) return {};
    return Object.fromEntries(searchParameters.entries());
  }, [searchParameters]);

  const query: Record<string, QueryValue> = useMemo(() => {
    const result: Record<string, QueryValue> = {};
    if (!searchParameters) return result;

    for (const [key, value] of searchParameters.entries()) {
      // App Router never returns arrays, but we keep API compatible
      if (key in result) {
        const previous = result[key];
        result[key] = Array.isArray(previous)
          ? [...previous, value]
          : [previous as string, value];
      } else {
        result[key] = value;
      }
    }

    return result;
  }, [pagesRouter, pagesRouter, searchParameters]);

  const push = useCallback(
    (
      pathname: string,
      query: Record<string, any> = {},
      options: NavOptions = {}
    ) => {
      if (pagesRouter) {
        const url =
          typeof pathname === "string" ? { pathname, query } : pathname;

        return pagesRouter.push(url, undefined, options);
      }

      if (options.shallow === true && typeof window !== "undefined") {
        const url = buildUrlFromPathAndQuery(pathname, query);
        window.history.pushState({}, "", url);
        // scroll control:
        if (options.scroll === false) {
          // do nothing (preserve scroll)
        } else {
          window.scrollTo({ top: 0 });
        }
        return;
      }

      const url = buildUrlFromPathAndQuery(pathname, {
        ...getAppCurrentQuery(),
        ...query
      });
      (appRouter as any).push(url, { scroll: options.scroll ?? true });
    },
    [appRouter, pagesRouter, getAppCurrentQuery]
  );

  const replace = useCallback(
    (
      pathname: string,
      query: Record<string, any> = {},
      options: NavOptions = {}
    ) => {
      if (pagesRouter) {
        return pagesRouter.replace(
          {
            pathname,
            query
          },
          undefined,
          options
        );
      }

      if (options.shallow === true && typeof window !== "undefined") {
        const url = buildUrlFromPathAndQuery(pathname, query);
        window.history.replaceState({}, "", url);
        if (options.scroll === false) {
          // preserve scroll
        } else {
          window.scrollTo({ top: 0 });
        }
        return;
      }

      const url = buildUrlFromPathAndQuery(pathname, {
        ...getAppCurrentQuery(),
        ...query
      });

      (appRouter as any).replace(url, { scroll: options.scroll ?? true });
    },
    [appRouter, pagesRouter, getAppCurrentQuery, pagesRouter]
  );
  return {
    ...pagesRouter,
    ...appRouter,
    query,
    pathname,
    push,
    replace,
    locale: "fa",
    isAppRouter: Boolean(pagesRouter)
  };
};
